import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import type { Subscription, Tenant } from '@etiquetachef/types';
import { TenantDisplayStatusBadge } from './TenantDisplayStatusBadge';
import { TenantMapEmbed } from './TenantMapEmbed';
import { CopyValue } from '@/components/CopyValue';
import { formatDate } from '@/lib/format';
import { useProfiles } from '@/features/members/useProfiles';

type Props = {
  tenant: Tenant;
  subscription: Subscription | null;
  billingPending?: boolean;
  billingError?: boolean;
  /**
   * Pedido de troca da flag de demonstração. O card segue apresentacional: a
   * confirmação e a chamada do callable ficam na `TenantDetailPage`.
   */
  onToggleDemo?: (nextValue: boolean) => void;
};

/** Par rótulo/valor do card. */
function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{value}</div>
    </div>
  );
}

/**
 * Card "Visão geral" do detalhe da empresa: dados cadastrais, status
 * comercial, contato do dono, endereço com mapa e interruptor de demo.
 *
 * Origem: sem issue — commit c5a7bd86a, 2026-04-26 16:52 +0200 (detalhe de
 * tenant com aba Visão geral, Sprint 2).
 *
 * Contrato: apresentacional — recebe o tenant e a assinatura já lidos pela
 * página (`useTenantDetail`); a troca de demo só sobe por `onToggleDemo` (a
 * confirmação é o `DemoFlagDialog` na página). A única leitura própria é o
 * perfil do fundador.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Visão geral).
 *
 * Dados: contato do OWNER vem de `profiles/{founderUid}` (via `useProfiles`)
 * — e-mail e telefone verificados vivem no perfil global, não no doc do
 * tenant. Tenant sem `founderUid` não mostra contato.
 *
 * Histórico:
 *  - 2026-06-27 14:51 +0200 — (sem issue, commit 10949c80e) passa a mostrar
 *    e-mail/telefone do owner, lidos do perfil (rules liberaram `profiles`
 *    para super-admin no mesmo commit).
 */
export function TenantInfoCard({
  tenant,
  subscription,
  billingPending = false,
  billingError = false,
  onToggleDemo,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [showMap, setShowMap] = useState(false);
  const address = tenant.address ?? '';
  // Contato do dono (OWNER): email e telefone verificados vivem no perfil
  // global do fundador (`/profiles/{founderUid}`), não no doc do tenant.
  const ownerProfile = useProfiles(tenant.founderUid ? [tenant.founderUid] : [])[
    tenant.founderUid ?? ''
  ];
  const mono = (v: React.ReactNode): React.JSX.Element => (
    <span style={{ fontFamily: 'monospace' }}>{v}</span>
  );

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={<CopyValue value={tenant.id} />}
          actions={
            <SpaceBetween direction="horizontal" size="s" alignItems="center">
              {onToggleDemo ? (
                <Toggle
                  checked={tenant.isDemo === true}
                  onChange={({ detail }): void => {
                    onToggleDemo(detail.checked);
                  }}
                >
                  {t('tenantDetail.demoFlag.label')}
                </Toggle>
              ) : null}
              <TenantDisplayStatusBadge
                tenant={tenant}
                subscription={subscription}
                billingPending={billingPending}
                billingError={billingError}
              />
            </SpaceBetween>
          }
        >
          {tenant.name}
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={2} variant="text-grid">
          <Field label={t('tenantDetail.info.kind')} value={t(`tenants.kind.${tenant.kind}`)} />
          <Field label={t('tenantDetail.info.country')} value={mono(tenant.countryCode ?? '—')} />
          <Field label={t('tenantDetail.info.locale')} value={mono(tenant.locale)} />
          <Field label={t('tenantDetail.info.timeZone')} value={mono(tenant.timeZone)} />
          <Field
            label={t('tenantDetail.info.taxId')}
            value={
              <span>
                {tenant.taxId ? <CopyValue value={tenant.taxId} /> : mono('—')}
                {tenant.needsTaxId && (
                  <Box variant="span" color="text-status-warning" fontSize="body-s">
                    {' '}{t('tenantDetail.info.needsTaxId')}
                  </Box>
                )}
              </span>
            }
          />
          <Field
            label={t('tenants.columns.createdAt')}
            value={formatDate(tenant.createdAt, i18n.language)}
          />
          {tenant.founderUid ? (
            <Field
              label={t('tenantDetail.info.ownerEmail')}
              value={ownerProfile?.email ? <CopyValue value={ownerProfile.email} /> : mono('—')}
            />
          ) : null}
          {tenant.founderUid ? (
            <Field
              label={t('tenantDetail.info.ownerPhone')}
              value={
                ownerProfile?.phoneE164 ? <CopyValue value={ownerProfile.phoneE164} /> : mono('—')
              }
            />
          ) : null}
        </ColumnLayout>

        <Field
          label={t('tenantDetail.info.address')}
          value={
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {address.length > 0 ? <CopyValue value={address} /> : <span>—</span>}
              <Button
                variant="inline-link"
                onClick={(): void => {
                  setShowMap(true);
                }}
              >
                {t('tenantDetail.info.viewLocation')}
              </Button>
            </SpaceBetween>
          }
        />
      </SpaceBetween>

      <Modal
        visible={showMap}
        onDismiss={(): void => {
          setShowMap(false);
        }}
        size="large"
        header={tenant.name}
      >
        <SpaceBetween size="s">
          {address.length > 0 ? <Box>{address}</Box> : null}
          <TenantMapEmbed tenant={tenant} />
        </SpaceBetween>
      </Modal>
    </Container>
  );
}
